import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Building2, Loader, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { profileData, walletBalance } from '../data/mockData';
export function Withdraw() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'form' | 'processing' | 'success'>('form');
  const [amount, setAmount] = useState('');
  const bank = profileData.bankDetails;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('processing');
    setTimeout(() => {
      setStep('success');
      setTimeout(() => {
        navigate('/wallet');
      }, 2000);
    }, 2500);
  };
  return <div className="min-h-screen" data-id="element-524">
      <div className="max-w-md mx-auto" data-id="element-525">
        {/* Header */}
        <div className="flex items-center gap-4 p-5 pt-8" data-id="element-526">
          <button onClick={() => navigate('/wallet')} className="w-10 h-10 rounded-full glass-card flex items-center justify-center text-white hover:bg-white/[0.08] transition-colors" data-id="element-527">
            <ArrowLeft className="w-5 h-5" data-id="element-528" />
          </button>
          <h1 className="text-white text-2xl font-bold tracking-tight" data-id="element-529">
            Withdraw
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="px-5 space-y-5" data-id="element-530">
          {/* Linked Bank Account */}
          {bank.isLinked ? <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="glass-card p-5" data-id="element-531">
              <p className="text-label mb-4" data-id="element-532">Payout Account</p>
              <div className="flex items-center gap-4" data-id="element-533">
                <div className="w-12 h-12 rounded-xl bg-[#00D68F]/10 flex items-center justify-center" data-id="element-534">
                  <Building2 className="w-6 h-6 text-[#00D68F]" data-id="element-535" />
                </div>
                <div className="flex-1" data-id="element-536">
                  <p className="text-white font-bold tracking-tight" data-id="element-537">
                    {bank.bankName}
                  </p>
                  <p className="text-[#64748B] font-mono text-sm" data-id="element-538">
                    {bank.accountNumber}
                  </p>
                  <p className="text-[#64748B] text-sm" data-id="element-539">{bank.accountName}</p>
                </div>
                <div className="flex items-center gap-1.5 bg-[#00D68F]/10 px-2.5 py-1 rounded-full" data-id="element-540">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#00D68F]" data-id="element-541" />
                  <span className="text-[#00D68F] text-xs font-bold" data-id="element-542">
                    Linked
                  </span>
                </div>
              </div>
            </motion.div> : <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-[#FF4757]/10 border border-[#FF4757]/20 rounded-2xl p-5" data-id="element-543">
              <div className="flex items-center gap-3 mb-3" data-id="element-544">
                <AlertCircle className="w-5 h-5 text-[#FF4757]" data-id="element-545" />
                <p className="text-[#FF4757] font-bold tracking-tight" data-id="element-546">
                  No Bank Account Linked
                </p>
              </div>
              <p className="text-[#FF4757]/70 text-sm mb-4" data-id="element-547">
                Link a bank account in your profile to withdraw.
              </p>
              <button type="button" onClick={() => navigate('/profile/bank')} className="text-[#FF4757] font-bold text-sm underline" data-id="element-548">
                Link Bank Account →
              </button>
            </motion.div>}

          {/* Amount */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="glass-card p-5" data-id="element-549">
            <p className="text-label mb-4" data-id="element-550">Withdrawal Amount</p>
            <div className="flex items-center gap-2 mb-3" data-id="element-551">
              <span className="text-[#64748B] text-2xl font-bold" data-id="element-552">₦</span>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" required min="1000" max={walletBalance.available} className="flex-1 bg-transparent text-white text-3xl font-bold tracking-tight focus:outline-none placeholder-[#475569] font-mono" data-id="element-553" />
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-white/[0.04]" data-id="element-554">
              <p className="text-[#64748B] text-xs font-medium" data-id="element-555">
                Available: ₦{walletBalance.available.toLocaleString()}
              </p>
              <button type="button" onClick={() => setAmount(walletBalance.available.toString())} className="text-[#00D68F] text-xs font-bold" data-id="element-556">
                MAX
              </button>
            </div>
          </motion.div>

          <motion.button initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} whileTap={{
          scale: 0.97
        }} type="submit" disabled={!bank.isLinked || !amount} className="w-full bg-[#00D68F] text-[#0A0E1A] rounded-2xl py-4 font-bold text-lg shadow-[0_0_20px_rgba(0,214,143,0.2)] disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-557">
            Withdraw Money
          </motion.button>

          <p className="text-[#64748B] text-xs text-center font-medium" data-id="element-558">
            Powered by Paystack • Instant bank transfer
          </p>
        </form>
      </div>

      {/* Processing / Success Modal */}
      <AnimatePresence data-id="element-559">
        {step !== 'form' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-[#0A0E1A]/90 backdrop-blur-xl flex items-center justify-center z-50 px-5" data-id="element-560">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} animate={{
          scale: 1,
          opacity: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          opacity: 0,
          y: 20
        }} className="glass-card border-white/[0.1] p-8 text-center max-w-sm w-full shadow-2xl" data-id="element-561">
              {step === 'processing' ? <>
                  <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'linear'
            }} className="w-20 h-20 mx-auto mb-6 flex items-center justify-center" data-id="element-562">
                    <Loader className="w-10 h-10 text-[#00D68F]" data-id="element-563" />
                  </motion.div>
                  <h3 className="text-white text-2xl font-bold tracking-tight mb-2" data-id="element-564">
                    Processing...
                  </h3>
                  <p className="text-[#64748B]" data-id="element-565">
                    Initiating bank transfer via API
                  </p>
                  <div className="mt-4 glass-card p-3 text-left" data-id="element-566">
                    <div className="flex justify-between text-sm mb-1" data-id="element-567">
                      <span className="text-[#64748B]" data-id="element-568">To:</span>
                      <span className="text-white font-medium" data-id="element-569">
                        {bank.bankName}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-570">
                      <span className="text-[#64748B]" data-id="element-571">Amount:</span>
                      <span className="text-[#00D68F] font-bold" data-id="element-572">
                        ₦{Number(amount).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </> : <>
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.2,
              type: 'spring',
              stiffness: 200
            }} className="w-20 h-20 rounded-full bg-[#00D68F]/10 flex items-center justify-center mx-auto mb-6" data-id="element-573">
                    <CheckCircle className="w-10 h-10 text-[#00D68F]" style={{
                filter: 'drop-shadow(0 0 8px rgba(0,214,143,0.5))'
              }} data-id="element-574" />
                  </motion.div>
                  <h3 className="text-white text-2xl font-bold tracking-tight mb-2" data-id="element-575">
                    Sent!
                  </h3>
                  <p className="text-[#64748B]" data-id="element-576">
                    ₦{Number(amount).toLocaleString()} is on the way to your{' '}
                    {bank.bankName} account
                  </p>
                </>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}